"use client";

import * as React from "react";
import useEmblaCarousel from "embla-carousel-react";
import TitleComponent from "../shared/TitleComponent";
import ArrowButton from "../shared/ArrowButton";
import { CustomLink } from "../ui/link";
import AccommodationCard from "./AccommodationCard";
import { Accommodation } from "@/types/accommodations";
import { DiscoverySection } from "@/types/discovery";
import { cn } from "@/lib/utils";
import { useCarouselScroll } from "@/components/carousel";

interface HomeCarouselSectionProps {
  title: string;
  data: Accommodation[];
  section?: DiscoverySection;
}

/**
 * Home page accommodation carousel section
 * Displays accommodation cards in a responsive RTL carousel
 */
const HomeCarouselSection = ({
  title,
  data,
  section,
}: HomeCarouselSectionProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    direction: "rtl",
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
  });

  const { canScrollPrev, canScrollNext, scrollPrev, scrollNext } =
    useCarouselScroll(emblaApi);

  return (
    <div className="space-y-6 pr-5! md:pr-0!">
      {/* Header with title, link, and navigation */}
      <div className="flex items-center justify-between md:gap-10">
        <TitleComponent title={title} />
        {section ? (
          <CustomLink
            href={`/discovery/${section}/search`}
            className="md:text-secondary-500 mr-auto md:font-normal md:text-sm text-primary-500 text-xs px-0 font-medium"
          >
            مشاهده همه
          </CustomLink>
        ) : null}
        <div className="flex items-center gap-4">
          <ArrowButton
            className={cn(
              "hidden md:!flex -scale-x-100",
              !canScrollPrev && "opacity-50 cursor-not-allowed",
            )}
            onClick={scrollPrev}
            disabled={!canScrollPrev}
          />
          <ArrowButton
            className={cn(
              "hidden md:!flex",
              !canScrollNext && "opacity-50 cursor-not-allowed",
            )}
            onClick={scrollNext}
            disabled={!canScrollNext}
          />
        </div>
      </div>

      {/* Carousel */}
      <div ref={emblaRef} className="overflow-hidden">
        <div
          className={cn(
            "flex",
            // Responsive gap
            "gap-8",
            // Responsive slide widths using CSS
            "[&>div]:flex-[0_0_calc(100%/1.2)] [&>div]:min-w-0",
            "sm:[&>div]:flex-[0_0_calc(100%/2.2)]",
            "md:[&>div]:flex-[0_0_calc(100%/3.2)]",
            "lg:[&>div]:flex-[0_0_calc(100%/3.2)]",
            "xl:[&>div]:flex-[0_0_25%] xl:gap-9",
          )}
        >
          {data.map((villa) => (
            <div key={villa.accommodationId} className="cursor-pointer">
              <AccommodationCard accommodation={villa} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomeCarouselSection;
